import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';

// Generic CRUD operations for Firestore
export class FirebaseService {
  constructor(collectionName) {
    this.collectionName = collectionName;
  }

  // Get user-specific collection reference
  getCollectionRef(userId) {
    return collection(db, 'users', userId, this.collectionName);
  }

  // Get user-specific document reference
  getDocRef(userId, docId) {
    return doc(db, 'users', userId, this.collectionName, docId);
  }

  // Create a new document
  async create(userId, data) {
    try {
      const docRef = await addDoc(this.getCollectionRef(userId), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...data };
    } catch (error) {
      console.error(`Error creating document in ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Read all documents for a user
  async getAll(userId) {
    try {
      const querySnapshot = await getDocs(this.getCollectionRef(userId));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error reading documents from ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Read a specific document
  async getById(userId, docId) {
    try {
      const docRef = this.getDocRef(userId, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error reading document from ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Update a document
  async update(userId, docId, data) {
    try {
      const docRef = this.getDocRef(userId, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      return { id: docId, ...data };
    } catch (error) {
      console.error(`Error updating document in ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Delete a document
  async delete(userId, docId) {
    try {
      const docRef = this.getDocRef(userId, docId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error deleting document from ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Query documents with filters
  async query(userId, filters = []) {
    try {
      let q = this.getCollectionRef(userId);
      
      // Ensure filters is an array
      const filterArray = Array.isArray(filters) ? filters : [];
      
      // Add filters to query
      filterArray.forEach(filter => {
        q = query(q, where(filter.field, filter.operator, filter.value));
      });
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying documents from ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Real-time listener for documents
  subscribeToCollection(userId, callback, filters = [], collectionName = null) {
    // Validate callback function
    if (typeof callback !== 'function') {
      console.error('Callback must be a function');
      return () => {}; // Return empty unsubscribe function
    }
    
    // Use specified collection or default collection
    const collectionRef = collectionName ? 
      collection(db, `users/${userId}/${collectionName}`) : 
      this.getCollectionRef(userId);
    
    let q = collectionRef;
    
    // Ensure filters is an array
    const filterArray = Array.isArray(filters) ? filters : [];
    
    // Add filters to query
    filterArray.forEach(filter => {
      q = query(q, where(filter.field, filter.operator, filter.value));
    });
    
    return onSnapshot(q, (querySnapshot) => {
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(data);
    });
  }

  // Real-time listener for a specific document
  subscribeToDocument(userId, docId, callback) {
    // Validate callback function
    if (typeof callback !== 'function') {
      console.error('Callback must be a function');
      return () => {}; // Return empty unsubscribe function
    }
    
    const docRef = this.getDocRef(userId, docId);
    
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }

  // Helper methods for different collections
  getCollection(userId, collectionName) {
    return collection(db, 'users', userId, collectionName);
  }

  async createInCollection(userId, collectionName, data) {
    try {
      const collectionRef = collection(db, 'users', userId, collectionName);
      const docRef = await addDoc(collectionRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...data };
    } catch (error) {
      console.error(`Error creating document in ${collectionName}:`, error);
      throw error;
    }
  }

  async updateInCollection(userId, collectionName, docId, data) {
    try {
      const docRef = doc(db, 'users', userId, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      return { id: docId, ...data };
    } catch (error) {
      console.error(`Error updating document in ${collectionName}:`, error);
      throw error;
    }
  }

  async deleteFromCollection(userId, collectionName, docId) {
    try {
      const docRef = doc(db, 'users', userId, collectionName, docId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error deleting document from ${collectionName}:`, error);
      throw error;
    }
  }

}

// Specific service instances for each component
export const foodService = new FirebaseService('food');
export const notesService = new FirebaseService('notes');
export const newsService = new FirebaseService('news');
export const sleepService = new FirebaseService('sleep');
export const booksService = new FirebaseService('books');
export const stocksService = new FirebaseService('stocks');
export const starredStocksService = new FirebaseService('starredStocks');
export const gymService = new FirebaseService('gym');

// News-specific services for reading history and bookmarks
export const newsReadingHistoryService = new FirebaseService('newsReadingHistory');
export const newsBookmarksService = new FirebaseService('newsBookmarks');

// Specialized news service with additional methods
export class NewsFirebaseService extends FirebaseService {
  constructor() {
    super('news');
  }

  // Add article to reading history
  async addToReadingHistory(userId, articleData) {
    try {
      const historyEntry = {
        articleId: articleData.id,
        title: articleData.title,
        description: articleData.description,
        url: articleData.url,
        source: articleData.source,
        category: articleData.category,
        publishedAt: articleData.publishedAt,
        readAt: serverTimestamp()
      };

      // Check if article already exists in reading history
      const existingHistory = await this.query(userId, [
        { field: 'articleId', operator: '==', value: articleData.id }
      ]);

      if (existingHistory.length === 0) {
        return await this.create(userId, historyEntry);
      } else {
        // Update the readAt timestamp
        return await this.update(userId, existingHistory[0].id, { readAt: serverTimestamp() });
      }
    } catch (error) {
      console.error('Error adding article to reading history:', error);
      throw error;
    }
  }

  // Get reading history with pagination
  async getReadingHistory(userId, limitCount = 50, startAfter = null) {
    try {
      let q = this.getCollectionRef(userId);
      q = query(q, orderBy('readAt', 'desc'));
      
      if (startAfter) {
        q = query(q, startAfter);
      }
      
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting reading history:', error);
      throw error;
    }
  }

  // Add article to bookmarks
  async addBookmark(userId, articleData) {
    try {
      const bookmarkEntry = {
        articleId: articleData.id,
        title: articleData.title,
        description: articleData.description,
        url: articleData.url,
        source: articleData.source,
        category: articleData.category,
        publishedAt: articleData.publishedAt,
        bookmarkedAt: serverTimestamp()
      };

      // Check if article is already bookmarked
      const existingBookmark = await this.query(userId, [
        { field: 'articleId', operator: '==', value: articleData.id }
      ]);

      if (existingBookmark.length === 0) {
        return await this.create(userId, bookmarkEntry);
      } else {
        throw new Error('Article already bookmarked');
      }
    } catch (error) {
      console.error('Error adding bookmark:', error);
      throw error;
    }
  }

  // Remove bookmark
  async removeBookmark(userId, articleId) {
    try {
      const existingBookmark = await this.query(userId, [
        { field: 'articleId', operator: '==', value: articleId }
      ]);

      if (existingBookmark.length > 0) {
        return await this.delete(userId, existingBookmark[0].id);
      } else {
        throw new Error('Bookmark not found');
      }
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  }

  // Check if article is bookmarked
  async isBookmarked(userId, articleId) {
    try {
      const existingBookmark = await this.query(userId, [
        { field: 'articleId', operator: '==', value: articleId }
      ]);
      return existingBookmark.length > 0;
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  }

  // Get bookmarks with pagination
  async getBookmarks(userId, limitCount = 50, startAfter = null) {
    try {
      let q = this.getCollectionRef(userId);
      q = query(q, orderBy('bookmarkedAt', 'desc'));
      
      if (startAfter) {
        q = query(q, startAfter);
      }
      
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting bookmarks:', error);
      throw error;
    }
  }

  // Get reading history for AI recommendations
  async getReadingHistoryForAI(userId, limitCount = 100) {
    try {
      const history = await this.getReadingHistory(userId, limitCount);
      return history.map(entry => ({
        title: entry.title,
        description: entry.description,
        category: entry.category,
        source: entry.source,
        publishedAt: entry.publishedAt,
        readAt: entry.readAt
      }));
    } catch (error) {
      console.error('Error getting reading history for AI:', error);
      throw error;
    }
  }

  // Clear old reading history (keep last 1000 entries)
  async cleanupReadingHistory(userId) {
    try {
      const allHistory = await this.getReadingHistory(userId, 1000);
      if (allHistory.length > 1000) {
        const toDelete = allHistory.slice(1000);
        for (const entry of toDelete) {
          await this.delete(userId, entry.id);
        }
      }
    } catch (error) {
      console.error('Error cleaning up reading history:', error);
    }
  }

  // Clear all reading history for a user
  async clearReadingHistory(userId) {
    try {
      const allHistory = await this.getAll(userId);
      for (const entry of allHistory) {
        await this.delete(userId, entry.id);
      }
    } catch (error) {
      console.error('Error clearing reading history:', error);
      throw error;
    }
  }
}

// Create specialized news service instance
export const newsFirebaseService = new NewsFirebaseService();

// Hook for using Firebase services with authentication
export const useFirebaseService = (service) => {
  const { user } = useAuth();
  
  if (!user) {
    throw new Error('User must be authenticated to use Firebase services');
  }
  
  return {
    create: (data) => service.create(user.uid, data),
    getAll: () => service.getAll(user.uid),
    getById: (docId) => service.getById(user.uid, docId),
    update: (docId, data) => service.update(user.uid, docId, data),
    delete: (docId) => service.delete(user.uid, docId),
    query: (filters = []) => service.query(user.uid, filters),
    subscribeToDocument: (docId, callback) => service.subscribeToDocument(user.uid, docId, callback),
    // Helper methods for different collections
    getCollection: (collectionName) => service.getCollection(user.uid, collectionName),
    createInCollection: (collectionName, data) => service.createInCollection(user.uid, collectionName, data),
    updateInCollection: (collectionName, docId, data) => service.updateInCollection(user.uid, collectionName, docId, data),
    deleteFromCollection: (collectionName, docId) => service.deleteFromCollection(user.uid, collectionName, docId),
    subscribeToCollection: (callback, filters = [], collectionName = null) => {
      return service.subscribeToCollection(user.uid, callback, filters, collectionName);
    }
  };
};

// Utility functions for data migration
export const migrateFromLocalStorage = async (service, localStorageKey, userId) => {
  try {
    const localData = localStorage.getItem(localStorageKey);
    if (localData) {
      const data = JSON.parse(localData);
      if (Array.isArray(data)) {
        // Migrate array of items
        for (const item of data) {
          await service.create(userId, item);
        }
      } else {
        // Migrate single item
        await service.create(userId, data);
      }
    }
  } catch (error) {
    console.error(`Error migrating ${localStorageKey} to Firebase:`, error);
  }
};

// Batch migration function
export const migrateAllData = async (userId) => {
  const migrations = [
    { service: foodService, key: 'foodWatcherData' },
    { service: notesService, key: 'notesAppData' },
    { service: newsService, key: 'newsTrackerData' },
    { service: sleepService, key: 'sleepTrackerData' },
    { service: booksService, key: 'booksTrackerData' },
    { service: stocksService, key: 'stocksWatcherData' },
    { service: gymService, key: 'gymSessionsData' }
  ];

  for (const migration of migrations) {
    await migrateFromLocalStorage(migration.service, migration.key, userId);
  }
};
